export const howspace = globalThis.howspace = {};

let isViewTransitioning = false;

howspace.getCssVariable = (elem, cssVariable) => {
    if (elem instanceof jQuery) {
        elem = elem[0];
    }
    return getComputedStyle(elem).getPropertyValue(cssVariable);
};

howspace.getAnimationDuration = (elem, cssVariable) => {
    const duration = howspace.getCssVariable(elem, cssVariable);
    if (!duration) {
        return 0;
    }
    if (!duration.endsWith('ms') && duration.endsWith('s')) {
        return parseFloat(duration) * 1000;
    }
    return parseFloat(duration);
};

/**
 * Whether a background of its own is light or dark enough that the see-through widgets on it need text of the
 * other kind, or null where their text already reads on it. The same as
 * NewUiVisuals::getTransparentWidgetTone(), so a colour being picked shows what saving it will.
 */
howspace.getTransparentWidgetTone = (color, elem) => {
    const rgba = colorString => {
        const probe = document.createElement('span');
        probe.style.color = colorString;
        if (probe.style.color === '') {
            return null;
        }

        document.body.appendChild(probe);
        const parts = getComputedStyle(probe).color.match(/[\d.]+/g);
        probe.remove();

        return parts === null || parts.length < 3 ? null : parts.map(parseFloat);
    };

    // ColorHelper::goodContrastForBlackText
    const isLight = ([r, g, b]) => {
        const luminance = [r, g, b]
            .map(channel => channel / 255)
            .map(value => value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4)
            .reduce((sum, value, i) => sum + value * [0.2126, 0.7152, 0.0722][i], 0);

        return (luminance + 0.05) / 0.05 > 1.05 / (luminance + 0.05);
    };

    const background = color ? rgba(color) : null;
    if (background === null || (background[3] ?? 1) < 0.5) {
        return null;
    }

    // The widgets otherwise have the text of the tone the element sits in, or the text of their own style.
    const outerTone = elem.parentElement?.closest('[data-background-tone]')?.dataset.backgroundTone;
    const text = outerTone
        ? rgba(outerTone === 'light' ? '#000000' : '#ffffff')
        : rgba(document.documentElement.dataset.transparentWidgetText || '');
    if (text !== null && isLight(text) !== isLight(background)) {
        return null;
    }

    return isLight(background) ? 'light' : 'dark';
};

/** Sets the tone a background gives the see-through widgets on it, or lets go of it. */
howspace.setTransparentWidgetTone = (elem, color) => {
    if (elem instanceof jQuery) {
        elem = elem[0];
    }

    const tone = howspace.getTransparentWidgetTone(color, elem);
    if (tone === null) {
        delete elem.dataset.backgroundTone;
    } else {
        elem.dataset.backgroundTone = tone;
    }
};

howspace.generateRandomStr = (length, keyspace = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ') => {
    let str = '';
    let max = keyspace.length - 1;
    for (let i = 0; i < length; ++i) {
        let index = Math.floor(Math.random() * (max + 1));
        str += keyspace[index];
    }
    return str;
};

howspace.generateRandomName = () => {
    const adjectives = [
        'Beautiful',
        'Strange',
        'Funny',
        'Cute',
        'Crazy',
        'Happy',
        'Sad',
        'Angry',
        'Silly',
        'Clever',
        'Smart',
        'Brave',
        'Strong',
        'Fast',
        'Slow',
        'Big',
        'Small',
        'Tiny',
        'Giant',
        'Short',
        'Tall',
        'Long',
        'Fat'
    ];

    const nouns = [
        'Dog',
        'Cat',
        'Bird',
        'Fish',
        'Mouse',
        'Rabbit',
        'House',
        'Car',
        'Bike',
        'Boat',
        'Plane',
        'Train',
        'Bus',
        'Rocket',
        'Star',
        'Planet',
        'Moon',
        'Sun',
        'Earth',
        'Mars',
        'Venus',
        'Mercury',
        'Jupiter',
        'Saturn',
        'Uranus',
        'Neptune',
        'Pluto',
        'Apple',
        'Banana',
        'Orange',
        'Lemon',
    ];

    const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    return adjective + ' ' + noun;
};

howspace.generateRandomWorkspaceName = () => {
    const adjectives = ['Cyber', 'Digital', 'Virtual', 'Augmented', 'Holographic', 'Neural', 'Quantum', 'Synthetic', 'Genetic', 'Biological', 'Robotic', 'Artificial', 'Nano', 'Molecular', 'Atomic', 'Cosmic', 'Interstellar', 'Intergalactic', 'Interplanetary', 'Interdimensional'];
    const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];

    const animals = ['Dolphin', 'Whale', 'Shark', 'Octopus', 'Squid', 'Jellyfish', 'Seahorse', 'Starfish', 'Crab', 'Lobster', 'Clam', 'Oyster', 'Coral', 'Anemone', 'Eel', 'Ray', 'Seal', 'Walrus', 'Manatee', 'Sea lion'];
    const animal = animals[Math.floor(Math.random() * animals.length)];

    return Date.now() + ' ' + adjective + ' ' + animal;
};

howspace.dispatchEvent = (elem, eventName, detail = {}) => {
    if (elem instanceof jQuery) {
        elem = elem[0];
    }
    elem.dispatchEvent(new CustomEvent(eventName, {
        detail : detail,
        bubbles : true
    }));
};

howspace.getClosestScrollingParent = elem => {
    if (elem instanceof jQuery) {
        elem = elem[0];
    }
    const parent = elem.parentElement;
    if (!parent) {
        // No more parents to check, return window.
        return real.dom.$window;
    }
    const style = getComputedStyle(parent);
    if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
        // Found a parent that has vertical scroll of "auto" or "scroll". It's enough to check just the
        // vertical scroll, because if set then overflowX cannot be "visible" according to the specs.
        return $(parent);
    }
    return howspace.getClosestScrollingParent(parent);
};

howspace.isFeatureSupported = feature => {
    return real.isFeatureSupported(feature);
};

howspace.isMicrosoftTeamsSession = () => {
    return real.dom.$body.hasClass('ms-teams');
};

howspace.isDuplicateError = response => {
    let isDuplicate = false;
    real.ajax.loopErrors(response, error => {
        if (error.code === 409) {
            isDuplicate = true;
        }
    });
    return isDuplicate;
};

howspace.isMobile = () => {
    return ('ontouchstart' in window) && window.screen.width < 992;
};

howspace.jsonCompare = (a, b) => {
    return JSON.stringify(a, Object.keys(a).sort()) === JSON.stringify(b, Object.keys(b).sort());
};

howspace.getDOMElement = (elem) => {
    if (elem instanceof jQuery) {
        elem = elem[0];
    }
    return elem;
};

howspace.focusFirstInput = false;

howspace.debounce = (func, delay) => {
    let timeout;
    return function(...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), delay);
    };
};

howspace.idleCallback = (fn) => {
    if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(fn);
    } else {
        setTimeout(fn, 1);
    }
};

howspace.viewTransition = (startFn, finishFn = null) => {
    if (!document.startViewTransition || isViewTransitioning) {
        startFn();
        if (typeof finishFn === 'function') {
            finishFn();
        }
        return;
    }

    isViewTransitioning = true;
    document.startViewTransition(startFn).finished.then(() => {
        if (typeof finishFn === 'function') {
            finishFn();
        }
        isViewTransitioning = false;
    });
};

howspace.splitTextToLines = text => {
    const MAX_LINES = 3;
    const TOKENS_PER_LINE = 3;
    const MAX_TOKENS = MAX_LINES * TOKENS_PER_LINE;

    // Intl.Segmenter for true grapheme clusters
    const segmenter = typeof Intl !== 'undefined' && Intl.Segmenter
        ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
        : null;
    const toGraphemes = str =>
        segmenter
            ? Array.from(segmenter.segment(str), s => s.segment)
            : Array.from(str);

    // Scripts that require splitting into graphemes:
    const SEGMENT_SCRIPTS = ['Han','Hiragana','Katakana','Hangul','Thai','Devanagari','Arabic','Hebrew'];
    const SEG_REGEX = new RegExp(
        SEGMENT_SCRIPTS.map(s => `\\p{Script=${s}}`).join('|'),
        'u'
    );

    // After join, collapse spaces only between characters of those scripts:
    const NOSP_RE = new RegExp(
        `([${SEGMENT_SCRIPTS.map(s => `\\p{Script=${s}}`).join('')}])\\s+([${SEGMENT_SCRIPTS.map(s => `\\p{Script=${s}}`).join('')}])`,
        'gu'
    );

    // 1) Rough split on whitespace
    const rough = text.match(/\S+/g) || [];

    // 2) For each token, only segment if it contains one of the SEG_REGEX scripts
    const tokens = [];
    for (const tok of rough) {
        if (SEG_REGEX.test(tok)) {
            for (const g of toGraphemes(tok)) tokens.push(g);
        } else {
            tokens.push(tok);
        }
    }

    // 3) Truncate to MAX_TOKENS and mark if we need an ellipsis
    const needsEllipsis = tokens.length > MAX_TOKENS;
    const slice = tokens.slice(0, MAX_TOKENS);

    // 4) Build up to 3 lines
    const lines = [];
    for (let i = 0; i < MAX_LINES; i++) {
        const chunk = slice.slice(i * TOKENS_PER_LINE, (i + 1) * TOKENS_PER_LINE);
        if (!chunk.length) break;
        let line = chunk.join(' ');
        // collapse spaces inside the segmented-script runs
        line = line.replace(NOSP_RE, '$1$2');
        if (i === MAX_LINES - 1 && needsEllipsis) {
            line += '…';
        }
        lines.push(line);
    }
    return lines;
};

real.onTierReady(real.TIER_2, () => {
    howspace.components.init(real.dom.$body);

    const ignoreWhenDisabled = (e) => {
        if (e.target.classList.contains('disabled') || e.target.closest('.disabled') !== null) {
            // Element or one of its ancestors has the "disabled" class. Click event is ignored.
            e.stopPropagation();
            e.preventDefault();
            return true;
        }
        return false;
    };

    // Middle click fires auxclick instead of click, so without this a disabled link still opens its href in a new tab.
    document.body.addEventListener('auxclick', ignoreWhenDisabled, {capture: true});

    document.body.addEventListener('click', (e) => {
        if (ignoreWhenDisabled(e)) {
            return;
        }

        if (e.isTrusted && !howspace.focusFirstInput) {
            howspace.focusFirstInput = e.pointerType === 'mouse' && e.button === 0;
            setTimeout(() => {
                howspace.focusFirstInput = false;
            });
        }
    }, {capture: true});

    document.body.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            howspace.focusFirstInput = true;
            setTimeout(() => {
                howspace.focusFirstInput = false;
            });

            if (e.target.dataset.toggle === 'dropdown') {
                return;
            }

            if (e.target.getAttribute('tabindex') === '0' || document.activeElement?.nodeName === 'BUTTON') {
                // Element is either a button or has a tabindex of 0, and the key pressed is Enter or Space. Trigger click.
                e.preventDefault();
                e.target.click();
            }
        }
    }, {capture: true});

    real.dom.$body.on('click', '.how-to-btn', e => {
        const btn = $(e.currentTarget);
        const articleId = btn.data('intercom-article-id');

        // If Intercom is defined and the article id is set, open the article.
        if (typeof window.Intercom === "function" && articleId) {
            e.preventDefault();
            Intercom('showArticle', articleId);
        }
    });

    real.events.on('session:logout', function(notification) {
        setTimeout(() => {
            let redirectUrl = real.baseUri;
            if (notification.object.user.email) {
                redirectUrl += '/?logged_out=1&email=' + encodeURIComponent(notification.object.user.email);
            } else if (notification.object.user.account_deleted){
                redirectUrl += '/?account_deleted=1';
            } else {
                redirectUrl += '/?logged_out=1';
            }
            window.location = redirectUrl;
        }, 500);
    });
});
